<template>
  <div class="page-vertical-container">
    <div class="page-header">
      <div class="page-header-left">
        <img src="@/assets/images/chatnav_logo.svg" alt="ChatNav Logo" class="dashboard-logo">
        <div class="title">User Management</div>
        <nav class="main-nav">
          <router-link to="/home" class="nav-link">Servers</router-link>
          <router-link to="/users" class="nav-link">Users</router-link>
          <router-link to="/admin" class="nav-link">Admin</router-link>
        </nav>
      </div>
      <div class="page-header-right">
        <div class="user-info">
          <span class="hub-admin-badge" title="You are a ChatNav Hub Admin">Hub Admin</span>
          <span class="username">{{ currentUsername }}</span>
          <button class="logout-btn" @click="logout">Logout</button>
        </div>
      </div>
    </div>

    <div class="page-content users-page">
      <div class="users-header">
        <h1>Users</h1>
        <button v-if="currentUsername === 'miro@chatnav.net'" @click="showCreateModal = true" class="btn-primary">
          Create User
        </button>
      </div>

      <div v-if="error" class="error-message">{{ error }}</div>

    <div class="users-table-container">
      <table class="users-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Admin</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.user_id">
            <td>{{ user.username }}</td>
            <td>
              <span class="badge" :class="{ 'badge-admin': user.hub_admin }">
                {{ user.hub_admin ? 'Admin' : 'User' }}
              </span>
            </td>
            <td class="actions">
              <button @click="editUser(user)" class="btn-edit" :disabled="user.hub_admin && currentUsername !== 'miro@chatnav.net' && user.user_id !== currentUserId">✏️ Edit</button>
              <button v-if="currentUsername === 'miro@chatnav.net'" @click="deleteUser(user)" class="btn-delete" :disabled="user.user_id === currentUserId">
                🗑️ Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create User Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal">
        <h2>Create New User</h2>
        <form @submit.prevent="createUser">
          <div class="form-group">
            <label>Email</label>
            <input v-model="newUser.username" type="email" required />
          </div>
          <div class="form-group">
            <label>Password</label>
            <input v-model="newUser.password" type="password" required minlength="6" />
          </div>
          <div class="form-group checkbox">
            <label>
              <input v-model="newUser.hub_admin" type="checkbox" />
              Admin User
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" @click="showCreateModal = false" class="btn-secondary">Cancel</button>
            <button type="submit" class="btn-primary">Create</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="showEditModal = false">
      <div class="modal">
        <h2>Edit User</h2>
        <form @submit.prevent="updateUser">
          <div class="form-group">
            <label>Email</label>
            <input v-model="editingUser.username" type="email" required />
          </div>
          <div class="form-group">
            <label>New Password (leave blank to keep current)</label>
            <input v-model="editingUser.password" type="password" minlength="6" autocomplete="new-password" />
          </div>
          <div class="form-group checkbox">
            <label>
              <input v-model="editingUser.hub_admin" type="checkbox" />
              Admin User
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" @click="showEditModal = false" class="btn-secondary">Cancel</button>
            <button type="submit" class="btn-primary">Save</button>
          </div>
        </form>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { apiFetch } from '@/services/api';
import { clearTokens } from '@/services/auth';

export default {
  name: 'UserManagement',
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      users: [],
      currentUserId: null,
      currentUsername: '',
      error: '',
      showCreateModal: false,
      showEditModal: false,
      newUser: {
        username: '',
        password: '',
        hub_admin: false
      },
      editingUser: {
        user_id: null,
        username: '',
        password: '',
        hub_admin: false
      }
    }
  },
  async mounted() {
    await this.getCurrentUser();
    await this.fetchUsers();
  },
  methods: {
    async getCurrentUser() {
      try {
        const response = await apiFetch('/api/me');

        if (response.ok) {
          const data = await response.json();
          this.currentUserId = data.user_id;
          this.currentUsername = data.username;

          // Redirect if not admin
          if (!data.hub_admin) {
            this.$router.push('/home');
          }
        }
      } catch (e) {
        console.error('Failed to get current user', e);
      }
    },

    async fetchUsers() {
      try {
        const response = await apiFetch('/api/users');

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        this.users = await response.json();
      } catch (e) {
        this.error = 'Failed to load users';
        console.error(e);
      }
    },

    async createUser() {
      try {
        const response = await apiFetch('/api/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.newUser)
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to create user');
        }

        this.toast.success('User created successfully');
        this.showCreateModal = false;
        this.newUser = { username: '', password: '', hub_admin: false };
        await this.fetchUsers();
      } catch (e) {
        this.toast.error(e.message);
      }
    },

    editUser(user) {
      this.editingUser = {
        user_id: user.user_id,
        username: user.username,
        password: '',
        hub_admin: user.hub_admin
      };
      this.showEditModal = true;
    },

    async updateUser() {
      // Build update payload (only include fields to update)
      const payload = {
        username: this.editingUser.username,
        hub_admin: this.editingUser.hub_admin
      };

      if (this.editingUser.password) {
        payload.password = this.editingUser.password;
      }

      try {
        const response = await apiFetch(`/api/users/${this.editingUser.user_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to update user');
        }

        this.toast.success('User updated successfully');
        this.showEditModal = false;
        await this.fetchUsers();
      } catch (e) {
        this.toast.error(e.message);
      }
    },

    async deleteUser(user) {
      if (user.user_id === this.currentUserId) {
        this.toast.error('Cannot delete your own account');
        return;
      }

      if (!confirm(`Are you sure you want to delete ${user.username}?`)) {
        return;
      }

      try {
        const response = await apiFetch(`/api/users/${user.user_id}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to delete user');
        }

        this.toast.success('User deleted successfully');
        await this.fetchUsers();
      } catch (e) {
        this.toast.error(e.message);
      }
    },

    logout() {
      clearTokens();
      this.$router.push('/');
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/css/home-page.scss";
@import "@/assets/css/user-management.scss";
</style>
